import { JSX, useMemo, useState } from "react";

type DataRow = Record<string, unknown>;

type InfoBarWidgetProps = {
  isLoading: boolean;
  data: unknown;
};

type SortState = { col: string; dir: "asc" | "desc" } | null;

const BAR_COLORS = [
  "#c0392b",
  "#1a7a7a",
  "#1e3a6e",
  "#565CA8",
  "#c4922a",
  "#048953",
  "#EF4B30",
  "#22324C",
  "#8e44ad",
  "#d35400",
];

const STACKED_COLORS = ["#059669", "#dc2626", "#2563eb", "#d97706"];

const isNumeric = (val: unknown): val is number =>
  typeof val === "number" && !isNaN(val);

const unwrap = (
  raw: unknown,
): { data?: DataRow[]; metadata?: Record<string, unknown> } => {
  if (!raw || typeof raw !== "object") return {};
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.data))
    return obj as { data: DataRow[]; metadata?: Record<string, unknown> };
  if (Array.isArray(obj)) return { data: obj as DataRow[] };
  return obj as { data?: DataRow[]; metadata?: Record<string, unknown> };
};

const getRateColor = (val: number): string => {
  if (val >= 70) return "#16a34a";
  if (val >= 40) return "#d97706";
  return "#dc2626";
};

const formatNum = (val: unknown): string => {
  if (!isNumeric(val)) return String(val ?? "–");
  return val % 1 === 0 ? val.toLocaleString() : val.toFixed(1);
};

const shortRateName = (col: string): string =>
  col.replace(/\s*(Rate|%|\(%\)|Pct)$/i, "").trim();

function compareValues(a: unknown, b: unknown, dir: "asc" | "desc"): number {
  const m = dir === "asc" ? 1 : -1;
  if (isNumeric(a) && isNumeric(b)) return (a - b) * m;
  return String(a ?? "").localeCompare(String(b ?? "")) * m;
}

function SortIcon({ col, sort }: { col: string; sort: SortState }) {
  const active = sort?.col === col;
  if (!active) return null;
  return (
    <span
      style={{
        display: "inline-block",
        marginLeft: 3,
        fontSize: 8,
      }}
    >
      {sort.dir === "asc" ? "▲" : "▼"}
    </span>
  );
}

export default function InfoBarWidget({
  isLoading,
  data,
}: InfoBarWidgetProps): JSX.Element {
  const parsed = useMemo(() => unwrap(data), [data]);
  const rows = parsed.data ?? [];
  const meta = parsed.metadata;
  const [sort, setSort] = useState<SortState>(null);

  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    return Object.keys(rows[0]);
  }, [rows]);

  const labelCol = useMemo(
    () =>
      columns.find((c) => rows.every((r) => !isNumeric(r[c]))) ?? columns[0],
    [columns, rows],
  );

  const rateCols = useMemo(
    () =>
      columns.filter(
        (c) =>
          c !== labelCol &&
          (c.toLowerCase().includes("rate") || c.includes("%")) &&
          rows.some((r) => isNumeric(r[c])),
      ),
    [columns, rows, labelCol],
  );

  const isStacked = rateCols.length > 1;

  const leadCols = useMemo(
    () =>
      columns.filter(
        (c) =>
          c !== labelCol &&
          !rateCols.includes(c) &&
          c.toLowerCase().includes("vol") &&
          rows.some((r) => isNumeric(r[c])),
      ),
    [columns, rows, labelCol, rateCols],
  );

  const extraCols = useMemo(
    () =>
      columns.filter(
        (c) =>
          c !== labelCol &&
          !rateCols.includes(c) &&
          !leadCols.includes(c) &&
          rows.some((r) => isNumeric(r[c])),
      ),
    [columns, rows, labelCol, rateCols, leadCols],
  );

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) =>
      compareValues(a[sort.col], b[sort.col], sort.dir),
    );
  }, [rows, sort]);

  const toggleSort = (col: string) => {
    setSort((prev) => {
      if (prev?.col === col) {
        return prev.dir === "desc" ? { col, dir: "asc" } : null;
      }
      return { col, dir: "desc" };
    });
  };

  const headerStyle: React.CSSProperties = {
    cursor: "pointer",
    userSelect: "none",
  };

  if (isLoading) {
    return (
      <div className="placeholder-glow h-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  if (rows.length === 0) {
    return <p className="text-muted small mb-0">No data available.</p>;
  }

  return (
    <div className="info-bar">
      {meta && Object.keys(meta).length > 0 && (
        <div className="info-bar__meta">
          {Object.entries(meta).map(([key, val]) => {
            if (typeof val === "string") {
              return (
                <span key={key} className="info-bar__meta-stat">
                  {key.replace(/_/g, " ")}: {val}
                </span>
              );
            }
            if (isNumeric(val)) {
              const label = key.replace(/^total_/, "").replace(/_/g, " ");
              const display = key.includes("pct")
                ? `${val.toFixed(1)}%`
                : val.toLocaleString();
              return (
                <span key={key} className="info-bar__meta-stat">
                  {label}: {display}
                </span>
              );
            }
            return null;
          })}
        </div>
      )}

      <div className="info-bar__header">
        <span
          className="info-bar__header-label"
          style={headerStyle}
          onClick={() => toggleSort(labelCol)}
        >
          {labelCol}
          <SortIcon col={labelCol} sort={sort} />
        </span>
        {rateCols.length > 0 && (
          <span className="info-bar__header-bar">
            {isStacked ? "Distribution" : rateCols[0]}
          </span>
        )}
        {leadCols.map((col) => (
          <span
            key={col}
            className="info-bar__header-extra"
            style={headerStyle}
            onClick={() => toggleSort(col)}
          >
            {col}
            <SortIcon col={col} sort={sort} />
          </span>
        ))}
        {rateCols.map((col, ci) => (
          <span
            key={col}
            className="info-bar__header-rate"
            style={{
              ...headerStyle,
              ...(isStacked
                ? { color: STACKED_COLORS[ci % STACKED_COLORS.length] }
                : {}),
            }}
            onClick={() => toggleSort(col)}
          >
            {isStacked ? shortRateName(col) : "Rate"}
            <SortIcon col={col} sort={sort} />
          </span>
        ))}
        {extraCols.map((col) => (
          <span
            key={col}
            className="info-bar__header-extra"
            style={headerStyle}
            onClick={() => toggleSort(col)}
          >
            {col}
            <SortIcon col={col} sort={sort} />
          </span>
        ))}
      </div>

      <div className="info-bar__list">
        {sortedRows.map((row, idx) => (
          <div key={idx} className="info-bar__row">
            <span className="info-bar__label">
              {String(row[labelCol] ?? "")}
            </span>

            {rateCols.length > 0 && (
              <span
                className={`info-bar__bar${isStacked ? " info-bar__bar--stacked" : ""}`}
              >
                {isStacked ? (
                  rateCols.map((col, ci) => {
                    const val = isNumeric(row[col]) ? (row[col] as number) : 0;
                    return (
                      <span
                        key={col}
                        className="info-bar__bar-segment"
                        style={{
                          flexGrow: val,
                          background:
                            STACKED_COLORS[ci % STACKED_COLORS.length],
                        }}
                      />
                    );
                  })
                ) : (
                  <span
                    className="info-bar__bar-fill"
                    style={{
                      width: `${Math.min(
                        isNumeric(row[rateCols[0]])
                          ? (row[rateCols[0]] as number)
                          : 0,
                        100,
                      )}%`,
                      background: BAR_COLORS[idx % BAR_COLORS.length],
                    }}
                  />
                )}
              </span>
            )}

            {leadCols.map((col) => (
              <span key={col} className="info-bar__extra">
                {formatNum(row[col])}
              </span>
            ))}

            {rateCols.map((col, ci) => {
              const val = isNumeric(row[col]) ? (row[col] as number) : 0;
              return (
                <span
                  key={col}
                  className="info-bar__rate"
                  style={{
                    color: isStacked
                      ? STACKED_COLORS[ci % STACKED_COLORS.length]
                      : getRateColor(val),
                  }}
                >
                  {formatNum(val)}%
                </span>
              );
            })}

            {extraCols.map((col) => (
              <span key={col} className="info-bar__extra">
                {formatNum(row[col])}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
